// Phase 23B — Bulk outlet transfer (admin/manager)
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Check, ArrowRightLeft, Upload, Search } from "lucide-react";
import { api, API_BASE, getToken, formatApiError } from "@/lib/api";
// Phase 26F.3 (closer attempt-2): Download-template handler is self-contained
// inline (fetch → blob → anchor); no longer imports `authenticatedDownload`
// from `useBulkJob` — bypasses shared helper to eliminate a whole class of
// bundling / tree-shake anomalies as failure modes.
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

export default function OutletTransfer() {
  const [beats, setBeats] = useState([]);
  const [sourceBeatId, setSourceBeatId] = useState("");
  const [targetBeatId, setTargetBeatId] = useState("");
  const [outlets, setOutlets] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [crossDist, setCrossDist] = useState(false);
  const [reason, setReason] = useState("");

  useEffect(() => {
    api.get("/beats", { params: { page_size: 200 } })
      .then(r => setBeats(r.data.items || []))
      .catch(e => toast.error(formatApiError(e)));
  }, []);

  const loadOutlets = useCallback(async () => {
    if (!sourceBeatId) { setOutlets([]); return; }
    setLoading(true);
    try {
      const r = await api.get("/retailers", { params: { beat_id: sourceBeatId, page_size: 200 } });
      setOutlets(r.data.items || []);
      setSelected(new Set());
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [sourceBeatId]);
  useEffect(() => { loadOutlets(); }, [loadOutlets]);

  const filtered = useMemo(() => {
    if (!q) return outlets;
    const lc = q.toLowerCase();
    return outlets.filter(o =>
      (o.code || "").toLowerCase().includes(lc) ||
      (o.name || "").toLowerCase().includes(lc) ||
      (o.phone || "").includes(lc)
    );
  }, [outlets, q]);

  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(o => o.id)));
  };

  const doTransfer = async () => {
    setSubmitting(true);
    try {
      const r = await api.post("/outlet-transfer", {
        outlet_ids: Array.from(selected),
        target_beat_id: targetBeatId,
        reason: reason || undefined,
        cross_distributor_ok: crossDist,
      });
      toast.success(`Transferred ${r.data.moved} outlet(s)` +
        (r.data.errors.length ? ` · ${r.data.errors.length} skipped` : ""));
      if (r.data.errors.length) {
        console.warn("Transfer errors:", r.data.errors);
      }
      setConfirmOpen(false);
      setSelected(new Set());
      await loadOutlets();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  const sourceBeat = beats.find(b => b.id === sourceBeatId);
  const targetBeat = beats.find(b => b.id === targetBeatId);

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="outlet-transfer-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 flex items-center gap-2">
            <ArrowRightLeft className="h-6 w-6 text-[#6D2C91]" /> Bulk Outlet Transfer
          </h1>
          <p className="text-xs text-neutral-500 mt-0.5">
            Move outlets between beats · every transfer logged to Section-C history · same-distributor by default.
          </p>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={async () => {
            // Phase 26F.3 (closer, attempt-2) — self-contained handler.
            // Bypasses `authenticatedDownload` in case the shared helper is
            // being tree-shaken / bundle-collided in production builds. Also
            // logs diagnostics so DevTools shows exactly what happened.
            // eslint-disable-next-line no-console
            console.log("[26f3-download] click fired", {
              hasToken: !!(localStorage.getItem("tulsi_token") || localStorage.getItem("token")),
              apiBase: process.env.REACT_APP_BACKEND_URL || "(undefined)",
            });
            try {
              const token = localStorage.getItem("tulsi_token")
                             || localStorage.getItem("token") || "";
              // API_BASE = REACT_APP_BACKEND_URL + /api/v1 (see lib/api.js) —
              // never build the versioned API URL by hand.
              const url = `${API_BASE}/retailers/bulk-transfer/template.xlsx`;
              const res = await fetch(url, {
                headers: { Authorization: `Bearer ${token}` },
              });
              if (!res.ok) throw new Error(`HTTP ${res.status}`);
              const blob = await res.blob();
              const objUrl = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = objUrl;
              a.download = "bulk_transfer_template.xlsx";
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(objUrl);
              toast.success("Template downloaded");
            } catch (e) {
              // eslint-disable-next-line no-console
              console.error("[26f3-download] failed", e);
              toast.error(`Download failed: ${e?.message || e}`);
            }
          }} data-testid="outlet-transfer-template-btn">
            <Upload className="h-4 w-4 mr-1 rotate-180" /> Download template
          </Button>
          <Button variant="outline" onClick={() => setImportOpen(true)}
                  data-testid="outlet-transfer-import-btn">
            <Upload className="h-4 w-4 mr-1" /> Import via Excel
          </Button>
        </div>
      </div>

      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-neutral-500">1. Source beat</Label>
            <SearchableSelect value={sourceBeatId} onChange={setSourceBeatId}
              options={beats.map(b => ({ id: b.id, code: b.code, name: b.name }))}
              placeholder="Pick source beat…" testid="outlet-transfer-source" />
          </div>
          <div>
            <Label className="text-xs text-neutral-500">3. Target beat</Label>
            <SearchableSelect value={targetBeatId} onChange={setTargetBeatId}
              options={beats.filter(b => b.id !== sourceBeatId).map(b => ({ id: b.id, code: b.code, name: b.name }))}
              placeholder="Pick target beat…" testid="outlet-transfer-target" />
          </div>
        </CardContent>
      </Card>

      {sourceBeatId && (
        <Card className="border-neutral-200 rounded-2xl">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-sm font-semibold text-neutral-900">
                2. Outlets in {sourceBeat?.code || "…"}
              </div>
              <Badge className="bg-neutral-100 text-neutral-700 text-xs" data-testid="outlet-transfer-count">
                {selected.size} of {filtered.length} selected
              </Badge>
              <div className="flex-1" />
              <div className="relative">
                <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-neutral-400" />
                <Input value={q} onChange={e => setQ(e.target.value)}
                        placeholder="search outlets…" className="pl-7 h-8 w-56 text-sm"
                        data-testid="outlet-transfer-search" />
              </div>
            </div>
            {loading && <div className="py-6 text-center"><Loader2 className="h-4 w-4 animate-spin mx-auto text-neutral-400" /></div>}
            {!loading && filtered.length === 0 && (
              <div className="py-8 text-center text-neutral-400 text-sm">No outlets on this beat.</div>
            )}
            {!loading && filtered.length > 0 && (
              <div>
                <div className="text-xs">
                  <button className="text-[#6D2C91] hover:underline" onClick={toggleAll} data-testid="outlet-transfer-select-all">
                    {selected.size === filtered.length ? "Clear selection" : "Select all filtered"}
                  </button>
                </div>
                <div className="max-h-96 overflow-auto border border-neutral-200 rounded mt-1">
                  {filtered.map(o => (
                    <label key={o.id}
                            className={`flex items-center gap-2 px-2 py-1.5 text-xs border-b last:border-b-0 ${
                              selected.has(o.id) ? "bg-[#6D2C91]/5" : "hover:bg-neutral-50"
                            }`}
                            data-testid={`outlet-row-${o.id}`}>
                      <Checkbox
                        checked={selected.has(o.id)}
                        onCheckedChange={(v) => {
                          setSelected(s => {
                            const n = new Set(s);
                            if (v) n.add(o.id); else n.delete(o.id);
                            return n;
                          });
                        }}
                        data-testid={`outlet-check-${o.id}`}
                      />
                      <span className="font-mono font-semibold text-neutral-900 min-w-[120px]">{o.code}</span>
                      <span className="text-neutral-700 flex-1 truncate">{o.name}</span>
                      <span className="text-neutral-500">{o.phone}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="flex justify-end pt-1">
              <Button className="bg-[#6D2C91] text-white hover:bg-[#5b2478]"
                       disabled={!targetBeatId || selected.size === 0}
                       onClick={() => setConfirmOpen(true)}
                       data-testid="outlet-transfer-review-btn">
                Review transfer → {targetBeat?.code || "…"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="outlet-transfer-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirm transfer</DialogTitle>
            <DialogDescription>
              You are about to move <b>{selected.size}</b> outlet(s) from <b>{sourceBeat?.code}</b> to <b>{targetBeat?.code}</b>.
              Cross-distributor moves are blocked unless admin bypass is checked.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <div>
              <Label className="text-xs">Reason (optional)</Label>
              <Input value={reason} onChange={e => setReason(e.target.value)}
                      placeholder="e.g. re-balancing coverage"
                      data-testid="outlet-transfer-reason" />
            </div>
            <label className="flex items-center gap-2 text-xs">
              <Checkbox checked={crossDist} onCheckedChange={setCrossDist}
                        data-testid="outlet-transfer-cross-dist" />
              Allow cross-distributor transfer (admin bypass)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button className="bg-[#6D2C91] text-white hover:bg-[#5b2478]"
                    onClick={doTransfer} disabled={submitting}
                    data-testid="outlet-transfer-confirm">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
              Transfer {selected.size} outlet(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImportDialog open={importOpen} onOpenChange={setImportOpen} onImported={loadOutlets} />
    </div>
  );
}

function ImportDialog({ open, onOpenChange, onImported }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [crossDist, setCrossDist] = useState(false);
  const [result, setResult] = useState(null);

  const doImport = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API_BASE}/outlet-transfer/import?cross_distributor_ok=${crossDist}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.error?.message || j.detail?.message || j.detail || `HTTP ${r.status}`);
      setResult(j);
      toast.success(`Imported · ${j.moved} moved · ${j.row_errors.length} errors`);
      if (onImported) await onImported();
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="outlet-transfer-import-dialog">
        <DialogHeader>
          <DialogTitle>Bulk import — outlet transfer</DialogTitle>
          <DialogDescription>
            CSV/XLSX with columns: <b>OUTLET_CODE, TARGET_BEAT_CODE</b>. Idempotent.
          </DialogDescription>
        </DialogHeader>
        <Input type="file" accept=".xlsx,.csv" onChange={e => setFile(e.target.files?.[0])}
                data-testid="outlet-transfer-import-file" />
        <label className="flex items-center gap-2 text-xs">
          <Checkbox checked={crossDist} onCheckedChange={setCrossDist}
                    data-testid="outlet-transfer-import-cross-dist" />
          Allow cross-distributor transfers
        </label>
        {result && (
          <div className="text-xs space-y-1">
            <div><b>Moved:</b> {result.moved} · <b>Errors:</b> {result.row_errors.length}</div>
            {result.row_errors.slice(0, 5).map(e => (
              <div key={e.row} className="text-rose-600">Row {e.row}: {e.error}</div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button className="bg-[#6D2C91] text-white hover:bg-[#5b2478]"
                  onClick={doImport} disabled={!file || busy}
                  data-testid="outlet-transfer-import-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}
            Import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
